import { isValidCategoryKey } from 'src/components/SetsPlayground/categoryKey';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;
type SetFilterContent = NonNullable<Models.SetsPlayground.SetFilterRequest['content']>;
type SetsConfigCultureGroup = Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup;

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidCalendarDate = (value: string): boolean => {
    if (!CALENDAR_DATE_PATTERN.test(value)) {
        return false;
    }

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCFullYear() === year
        && date.getUTCMonth() === month - 1
        && date.getUTCDate() === day;
};

export const getDistributionValidationError = (distribution: SetDistribution | null | undefined): string | null => {
    if (distribution?.type === 'positions') {
        const positions = distribution.positions ?? [];

        if (positions.length === 0) {
            return 'Positions distribution needs at least one position';
        }

        if (positions.some((position) => !Number.isInteger(position) || position < 1)) {
            return 'Positions must be whole numbers of 1 or more';
        }
    }

    return null;
};

const CREATED_DATE_LABELS: Record<'MinCreatedDate' | 'MaxCreatedDate', string> = {
    MinCreatedDate: 'Min Created Date',
    MaxCreatedDate: 'Max Created Date',
};

export const getContentFilterValidationErrors = (content: SetFilterContent | undefined): string[] => {
    if (!content) {
        return [];
    }

    const errors: string[] = [];

    (['MinCreatedDate', 'MaxCreatedDate'] as const).forEach((key) => {
        const values = content[key] ?? [];

        if (values.length === 0) {
            return;
        }

        if (values.length > 1 || !isValidCalendarDate(values[0])) {
            errors.push(`${CREATED_DATE_LABELS[key]} must be a single valid date (YYYY-MM-DD)`);
        }
    });

    return errors;
};

export const getCategoryFilterValidationError = (category: string[][] | undefined): string | null => {
    const hasInvalidKey = (category ?? []).some((group) => group.some((key) => !isValidCategoryKey(key)));

    return hasInvalidKey ? 'Category keys must be whole numbers, not category IDs or names' : null;
};

export const getSetValidationErrors = (set: SetDefinition): string[] => {
    const distributionError = getDistributionValidationError(set?.distribution);
    const categoryError = getCategoryFilterValidationError(set?.filters?.category);

    return [
        ...(distributionError ? [distributionError] : []),
        ...getContentFilterValidationErrors(set?.filters?.content),
        ...(categoryError ? [categoryError] : []),
    ];
};

export const getNewnessDistributionValidationErrors = (
    distribution: SetDistribution | null | undefined,
): string[] => {
    if (!distribution) {
        return [];
    }

    const error = getDistributionValidationError(distribution);

    return error ? [error] : [];
};

export const getSeasonalityDistributionValidationErrors = getNewnessDistributionValidationErrors;

export const groupHasValidationErrors = (group: SetsConfigCultureGroup): boolean => {
    const hasSetErrors = group.sets.some((set) => getSetValidationErrors(set).length > 0);
    const hasNewnessErrors = group.newnessEnabled
        && getNewnessDistributionValidationErrors(group.newnessDistribution).length > 0;

    const hasSeasonalityErrors = group.seasonalityEnabled
        && getSeasonalityDistributionValidationErrors(group.seasonalityDistribution).length > 0;

    return hasSetErrors || hasNewnessErrors || hasSeasonalityErrors;
};
